// Utility function for debouncing
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// Utility function for handling Escape key
function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.focus();
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) {
      facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      productGridContainer.querySelector('.collection')?.classList.add('loading');
    }

    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(html);
        }
      })
      .catch((error) => {
        console.error('Error fetching filter data:', error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(html);
    }
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGridContainer) return;

    productGridContainer.innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    productGridContainer.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    let appliedCountContainer = document.querySelector('#FacetFiltersApplied');

    // Calculate total active filters
    let totalActiveFilters = 0;
    const filters = parsedHTML.querySelectorAll(
      '.active-facets-desktop .active-facets__button:not(.active-facets__button-remove)'
    );
    filters.forEach(() => {
      totalActiveFilters += 1;
    });

    // Ensure FacetFiltersApplied span exists
    if (!appliedCountContainer) {
      appliedCountContainer = document.createElement('span');
      appliedCountContainer.id = 'FacetFiltersApplied';
      appliedCountContainer.className = 'facet-filters-applied';
      if (containerDesktop) {
        containerDesktop.insertBefore(appliedCountContainer, containerDesktop.firstChild);
      } else if (container) {
        container.parentElement.insertBefore(appliedCountContainer, container);
      }
    }

    // Update the applied filters count
    appliedCountContainer.innerHTML = `${totalActiveFilters} filter${totalActiveFilters !== 1 ? 's' : ''} applied | `;


    // Get product count text
    const productCountText = parsedHTML.getElementById('ProductCount')?.innerHTML || '';

    // Update containers
    if (containerDesktop) {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const updatedCount = parsedHTML.getElementById('ProductCountDesktop');
    
      if (updatedCount) {
        containerDesktop.innerHTML = updatedCount.innerHTML;
      }
    
      containerDesktop.classList.remove('loading');
    }
    if (container) {
      container.innerHTML = productCountText;
      container.classList.remove('loading');
    }

    // Hide loading spinners
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
  
    // Remove facets no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });
  
    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };
  
    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);
  
    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }
        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`)?.before(elementToRender);
        }
      }
    });
  
    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
  
    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter')?.id;
      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));
  
        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const isMobile = newFacetDetailsElement?.classList.contains('facets__disclosure');
        const newElementSelector = isMobile ? `.facets__summary` : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement?.querySelector(newElementSelector);
        const isTextInput = event.target.getAttribute('type') === 'text';
  
        // Close the details element on mobile after applying a filter
        if (isMobile && newFacetDetailsElement) {
          newFacetDetailsElement.removeAttribute('open');
        }
  
        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }
  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];
    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (activeFacetsElement) {
        document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
      }
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];
    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (sourceElement && targetElement) {
        targetElement.innerHTML = sourceElement.innerHTML;
      }
    });

    // Safely handle menu-drawer binding
    const facetFiltersFormMobile = document.getElementById('FacetFiltersFormMobile');
    const menuDrawer = facetFiltersFormMobile?.closest('menu-drawer');
    if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
      menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');
    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');
    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }
      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');
    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    return [
      {
        section: productGrid ? productGrid.dataset.id : '',
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.target.className.includes('mobile-facets__checkbox')) {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';
      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'].includes(form.id)) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') === -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
    FacetFiltersForm.renderActiveFacets(new DOMParser().parseFromString(document.documentElement.innerHTML, 'text/html'));
    FacetFiltersForm.renderProductCount(document.documentElement.innerHTML);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}
customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (facetLink) {
      facetLink.setAttribute('role', 'button');
      facetLink.addEventListener('click', this.closeFilter.bind(this));
      facetLink.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
      });
    }
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form) form.onActiveFilterClick(event);
  }
}
customElements.define('facet-remove', FacetRemove);

// Mobile Facets Display Fixes

// 1. Add a close button to facets display on mobile
document.addEventListener('DOMContentLoaded', function() {
  if (window.matchMedia("(max-width: 769px)").matches) {
  // Function to add close buttons and headers to facet displays
  function addCloseButtonsToFacets() {
    // Target only mobile facet disclosures
    const mobileFacetDisclosures = document.querySelectorAll('#FacetsWrapperMobile .facets__disclosure');
    
    mobileFacetDisclosures.forEach(disclosure => {
      const facetsDisplay = disclosure.querySelector('.facets__display');
      const summaryLabel = disclosure.querySelector('.facets__summary-label')?.textContent || 'Filter Options';
      
      // Check if the display already has a close button to avoid duplicates
      if (facetsDisplay && !facetsDisplay.querySelector('.facet-close-btn')) {
        // Create close button element
        const closeButton = document.createElement('button');
        closeButton.className = 'facet-close-btn';
        closeButton.setAttribute('aria-label', 'Close filter options');
        closeButton.innerHTML = '×'; // X character
        
        // Create modal header
        if (!facetsDisplay.querySelector('.facet-modal-header')) {
          const modalHeader = document.createElement('div');
          modalHeader.className = 'facet-modal-header';
          modalHeader.textContent = summaryLabel;
          facetsDisplay.insertBefore(modalHeader, facetsDisplay.firstChild);
        }
        
        // Add to the beginning of the facets display
        facetsDisplay.insertBefore(closeButton, facetsDisplay.firstChild);
        
        // Add event listener to close button
        closeButton.addEventListener('click', function(event) {
          event.preventDefault();
          event.stopPropagation();
          disclosure.removeAttribute('open');
          document.body.style.overflow = ''; // Restore scrolling
        });
      }
    });
  }

  // 2. Fix the positioning of dropdowns on mobile
  function fixMobileDropdownPositioning() {
    const mobileFacetDisclosures = document.querySelectorAll('#FacetsWrapperMobile .facets__disclosure');
    
    mobileFacetDisclosures.forEach(disclosure => {
      const summary = disclosure.querySelector('.facets__summary');
      const display = disclosure.querySelector('.facets__display');
      
      if (summary && display) {
        // Add event listener for when the details element is opened
        disclosure.addEventListener('toggle', function() {
          if (this.hasAttribute('open')) {
            // Get the position of the summary button
            const summaryRect = summary.getBoundingClientRect();
            
            // Apply modal-like styling
            document.body.style.overflow = 'hidden'; // Prevent scrolling behind the modal
            
            // No need to manually position - CSS will handle this as a fixed centered element
          } else {
            // Restore body scrolling when facet is closed
            document.body.style.overflow = '';
          }
        });
      }
    });
  }
  
  // Initialize and re-initialize when filters are rendered
  addCloseButtonsToFacets();
  fixMobileDropdownPositioning();
  
  // Observer for dynamic changes to the facets wrapper
  const facetsWrapper = document.getElementById('FacetsWrapperMobile');
  if (facetsWrapper) {
    const observer = new MutationObserver(function() {
      addCloseButtonsToFacets();
      fixMobileDropdownPositioning();
    });
    
    observer.observe(facetsWrapper, { childList: true, subtree: true });
  }
  
  // Also handle when facets are rendered via AJAX
  document.addEventListener('facetsRendered', function() {
    addCloseButtonsToFacets();
    fixMobileDropdownPositioning();
  });
}
});